import { tenantListResponseSchema, type TenantListResponse } from '@console/types'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { apiGet, apiPut } from '../api.js'
import { fetchCsrfToken } from '../auth/api.js'

const selectTenantResponseSchema = z.object({ currentTenantId: z.string() })

/**
 * The tenant registry, read from Cadence through the console's proxy.
 *
 * Deliberately NOT persisted anywhere — not in the console's database, not in
 * localStorage. The founder's ruling of 2026-08-22 makes Cadence the registry,
 * and a cached copy that outlives the request is a second store that can
 * disagree with it. React Query's in-memory cache is bounded by the tab.
 *
 * `staleTime` is short because the switcher is opened when someone believes
 * something has changed — a tenant onboarded a minute ago should be there.
 */
export function useTenants() {
  return useQuery<TenantListResponse>({
    queryKey: ['tenants'],
    queryFn: () => apiGet('/tenants', tenantListResponseSchema),
    staleTime: 30_000,
  })
}

/**
 * Change the workspace this session is working in.
 *
 * Invalidates EVERYTHING on success, not just the tenant query. Every screen's
 * data is a function of the selected workspace, so a cache left warm would
 * paint the previous workspace's numbers under the new one's name — the whole
 * failure this feature exists to remove, reintroduced by a stale query.
 */
export function useSelectTenant() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (tenantId: string) => {
      const csrfToken = await fetchCsrfToken()
      return apiPut('/session/tenant', { tenantId }, csrfToken, selectTenantResponseSchema)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries()
    },
  })
}
